<template>
  <div class="min-h-screen bg-gray-50 dark:bg-neutral-900">
    <Breadcrumb :items="[{ title: 'CV Havuzu', path: '/cv-pool' }]" />
    
    <!-- Hero Section -->
    <div class="relative bg-gradient-to-r from-primary to-primary/80 h-[300px] overflow-hidden">
      <div class="absolute inset-0">
        <div class="absolute inset-0 bg-[url('/assets/images/pattern-grid.svg')] opacity-10"></div>
        <div class="absolute inset-0 bg-gradient-to-b from-black/10 to-transparent"></div>
      </div>
      <div class="relative container mx-auto px-4 h-full flex items-center">
        <div class="max-w-2xl text-white">
          <h1 class="text-4xl md:text-5xl font-bold mb-4">{{ t('cvPool.title') }}</h1>
          <p class="text-lg md:text-xl opacity-90">{{ t('cvPool.description') }}</p>
        </div>
      </div>
    </div>

    <!-- Form Section -->
    <div class="py-16">
      <div class="container mx-auto px-4">
        <div class="max-w-3xl mx-auto">
          <div class="bg-white dark:bg-neutral-800 rounded-2xl p-8 shadow-lg">
            <form @submit.prevent="handleSubmit" class="space-y-8">
              <!-- İsim Soyisim -->
              <div class="form-group">
                <label class="form-label">{{ t('cvPool.fullName') }}</label>
                <input type="text" 
                       v-model="form.fullName" 
                       :class="[
                         'form-input',
                         errors.fullName ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''
                       ]"
                       @blur="validateField('fullName')"
                       :placeholder="t('cvPool.fullName')"
                       required />
                <span v-if="errors.fullName" class="error-message">{{ errors.fullName }}</span>
              </div>

              <!-- Email -->
              <div class="form-group">
                <label class="form-label">{{ t('contact.email') }}</label>
                <input type="email" 
                       v-model="form.email" 
                       :class="[
                         'form-input',
                         errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''
                       ]"
                       @blur="validateField('email')"
                       placeholder="ornek@email.com"
                       required />
                <span v-if="errors.email" class="error-message">{{ errors.email }}</span>
              </div>

              <!-- Meslek Seç -->
              <div class="form-group" ref="professionDropdownRef">
                <label class="form-label">{{ t('cvPool.profession') }}</label>
                <div class="relative">
                  <button type="button"
                    @click="isOpenProfession = !isOpenProfession"
                    :class="[
                      'form-select',
                      errors.profession ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''
                    ]">
                    <span class="block truncate">{{ form.profession ? professionOptions.find(p => p.value === form.profession)?.label : t('cvPool.selectProfession') }}</span>
                    <span class="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                      <svg class="w-4 h-4 text-gray-500 transition-transform duration-300" :class="{ 'rotate-180': isOpenProfession }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </span>
                  </button>
                  <span v-if="errors.profession" class="error-message">{{ errors.profession }}</span>
                  
                  <div v-if="isOpenProfession" 
                       class="dropdown-menu">
                    <div class="max-h-60 overflow-y-auto custom-scrollbar">
                      <button type="button"
                              v-for="option in professionOptions" 
                              :key="option.value"
                              @click="selectProfession(option.value)"
                              class="dropdown-item">
                        {{ option.label }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Diğer Meslek -->
              <div v-if="form.profession === 'other'" class="form-group">
                <label class="form-label">{{ t('cvPool.otherProfession') }}</label>
                <input type="text" 
                       v-model="form.otherProfession" 
                       :class="[
                         'form-input',
                         errors.otherProfession ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''
                       ]"
                       @blur="validateField('otherProfession')"
                       :placeholder="t('cvPool.otherProfession')"
                       required />
                <span v-if="errors.otherProfession" class="error-message">{{ errors.otherProfession }}</span>
              </div>

              <!-- İş/Staj Seçimi -->
              <div class="flex items-center justify-center space-x-4">
                <button type="button"
                        @click="form.applicationType = 'job'"
                        class="flex-1 py-3 px-4 rounded-xl border-2 transition-all duration-200 flex items-center justify-center gap-2"
                        :class="form.applicationType === 'job' ? 'border-primary bg-primary/5 text-primary' : 'border-gray-200 dark:border-neutral-600 hover:border-primary/50'">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>{{ t('cvPool.jobType.job') }}</span>
                </button>
                <button type="button"
                        @click="form.applicationType = 'internship'"
                        class="flex-1 py-3 px-4 rounded-xl border-2 transition-all duration-200 flex items-center justify-center gap-2"
                        :class="form.applicationType === 'internship' ? 'border-primary bg-primary/5 text-primary' : 'border-gray-200 dark:border-neutral-600 hover:border-primary/50'">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  <span>{{ t('cvPool.jobType.internship') }}</span>
                </button>
              </div>

              <!-- CV Yükleme Alanı -->
              <div class="relative">
                <div class="group border-2 border-dashed border-gray-300 dark:border-neutral-600 rounded-xl p-8 text-center hover:border-primary transition-colors"
                     :class="{ 'border-primary bg-primary/5': isDragging }"
                     @dragenter.prevent="isDragging = true"
                     @dragleave.prevent="isDragging = false"
                     @dragover.prevent
                     @drop.prevent="handleDrop">
                  
                  <input type="file" 
                         ref="fileInput"
                         class="hidden" 
                         @change="handleFileSelect"
                         accept=".pdf,.doc,.docx" />
                  
                  <div v-if="!form.cv" class="space-y-4">
                    <div class="w-16 h-16 mx-auto rounded-xl bg-primary/10 flex items-center justify-center">
                      <svg class="w-8 h-8 text-primary transition-transform duration-300 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <p class="text-lg font-medium text-gray-900 dark:text-white">{{ t('cvPool.cv.upload') }}</p>
                      <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">{{ t('cvPool.cv.format') }}</p>
                    </div>
                    <button type="button"
                            @click="$refs.fileInput.click()"
                            class="inline-flex items-center px-4 py-2 text-sm font-medium text-primary hover:text-white bg-primary/10 hover:bg-primary rounded-lg transition-all duration-300">
                      <span>{{ t('cvPool.cv.select') }}</span>
                      <svg class="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                    </button>
                  </div>

                  <div v-else class="flex items-center justify-between p-4 bg-primary/5 rounded-lg">
                    <div class="flex items-center">
                      <svg class="w-8 h-8 text-primary mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div class="text-left">
                        <p class="text-sm font-medium text-gray-900 dark:text-white">{{ form.cv.name }}</p>
                        <p class="text-xs text-gray-500 dark:text-gray-400">{{ formatFileSize(form.cv.size) }}</p>
                      </div>
                    </div>
                    <button type="button" 
                            @click="form.cv = null"
                            class="text-red-500 hover:text-red-600 transition-colors">
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Kullanıcı Sözleşmesi -->
              <div class="flex items-start">
                <div class="flex items-center h-5">
                  <input type="checkbox" 
                         v-model="form.agreement"
                         class="w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-primary/20 dark:bg-gray-700 dark:border-gray-600 dark:focus:ring-primary/20"
                         required />
                </div>
                <label class="ml-2 text-sm text-gray-600 dark:text-gray-400">
                  {{ t('cvPool.agreement') }}
                </label>
              </div>

              <!-- Submit Button -->
              <button type="submit" 
                      class="w-full bg-primary text-white py-4 rounded-xl hover:bg-primary/90 transition-colors flex items-center justify-center group disabled:opacity-50 disabled:cursor-not-allowed"
                      :disabled="!isFormValid">
                <span>Gönder</span>
                <svg class="w-5 h-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Kullanıcı Sözleşmesi Modal -->
    <TransitionRoot appear :show="showAgreement" as="template">
      <Dialog as="div" @close="showAgreement = false" class="relative z-50">
        <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        </TransitionChild>

        <div class="fixed inset-0 overflow-y-auto">
          <div class="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95"
            >
              <DialogPanel class="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white dark:bg-neutral-800 p-6 text-left align-middle shadow-xl transition-all">
                <DialogTitle as="h3" class="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4">
                  Kullanıcı Sözleşmesi
                </DialogTitle>

                <div class="mt-2 prose dark:prose-invert max-h-96 overflow-y-auto custom-scrollbar">
                  <p>Lorem ipsum dolor sit amet consectetur adipisicing elit. Maxime mollitia,
                  molestiae quas vel sint commodi repudiandae consequuntur voluptatum laborum
                  numquam blanditiis harum quisquam eius sed odit fugiat iusto.</p>
                  
                  <h4>1. Kişisel Verilerin İşlenmesi</h4>
                  <p>Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium,
                  totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae
                  dicta sunt explicabo.</p>

                  <h4>2. Veri Güvenliği</h4>
                  <p>Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit,
                  sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt.</p>

                  <h4>3. Kullanım Koşulları</h4>
                  <p>Neque porro quisquam est, qui dolorem ipsum quia dolor sit amet, consectetur,
                  adipisci velit, sed quia non numquam eius modi tempora incidunt ut labore et dolore
                  magnam aliquam quaerat voluptatem.</p>
                </div>

                <div class="mt-6 flex justify-end">
                  <button
                    type="button"
                    class="inline-flex justify-center rounded-lg border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2"
                    @click="showAgreement = false"
                  >
                    Tamam
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { Dialog, DialogPanel, DialogTitle, TransitionRoot, TransitionChild } from '@headlessui/vue'
import { onClickOutside } from '@vueuse/core'
import { useFileDialog } from '@vueuse/core'
import { useToast } from 'vue-toastification'
import { useCV } from '~/composables/useCV'
import { useI18n } from 'vue-i18n'

// i18n
const { t } = useI18n()

// Toast bildirimleri
const toast = useToast()

const { files, open, reset } = useFileDialog({
  accept: '.pdf,.doc,.docx', // Kabul edilen dosya türleri
  multiple: false
})

// CV composable'ını kullan
const { form: cvForm, isLoading, submitCV, setFile } = useCV()

// Form alanları
const form = ref({
  fullName: '',
  email: '',
  phone: '',
  profession: '',
  otherProfession: '',
  applicationType: '',
  cv: null,
  agreement: false
})

const fileInput = ref(null)
const isDragging = ref(false)
const showAgreement = ref(false)
const isOpenProfession = ref(false)
const professionDropdownRef = ref(null)

const isOtherProfessionSelected = computed(() => form.value.profession === 'other')

// Meslek seçenekleri
const professionOptions = [
  { value: 'software', label: 'Yazılım Geliştirici' },
  { value: 'design', label: 'Tasarımcı' },
  { value: 'marketing', label: 'Pazarlama Uzmanı' },
  { value: 'sales', label: 'Satış Uzmanı' },
  { value: 'hr', label: 'İnsan Kaynakları' },
  { value: 'finance', label: 'Finans Uzmanı' },
  { value: 'other', label: 'Diğer' }
]

// Dışarı tıklama işleyicisi
onClickOutside(professionDropdownRef, () => {
  isOpenProfession.value = false
})

// Meslek seçimi
const selectProfession = (value) => {
  form.value.profession = value
  if (value !== 'other') {
    form.value.otherProfession = ''
  }
  isOpenProfession.value = false
}

// Dosya boyutu formatı
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// Dosya seçimi
const handleFileSelect = (event) => {
  const file = event.target.files[0]
  if (file) {
    validateAndSetFile(file)
  }
}

// Sürükle-bırak
const handleDrop = (event) => {
  isDragging.value = false
  const file = event.dataTransfer.files[0]
  if (file) {
    validateAndSetFile(file)
  }
}

// Dosya doğrulama
const validateAndSetFile = (file) => {
  const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
  const maxSize = 5 * 1024 * 1024 // 5MB

  if (!validTypes.includes(file.type)) {
    toast.error('Lütfen PDF veya Word dosyası yükleyin')
    return
  }

  if (file.size > maxSize) {
    toast.error('Dosya boyutu 5MB\'dan büyük olamaz')
    return
  }

  form.value.cv = file
  // useCV composable'ına dosyayı gönder
  setFile(file)
  toast.success('CV başarıyla yüklendi')
}

// Form geçerliliği
const isFormValid = computed(() => {
  return form.value.fullName &&
         form.value.email &&
         form.value.profession &&
         (form.value.profession !== 'other' || form.value.otherProfession) &&
         form.value.applicationType &&
         form.value.cv &&
         form.value.agreement
})

const errors = ref({
  fullName: '',
  email: '',
  profession: '',
  otherProfession: '',
  cv: ''
})

const validateField = (field) => {
  errors.value[field] = ''

  switch (field) {
    case 'fullName':
      if (!form.value.fullName) {
        errors.value.fullName = 'İsim Soyisim alanı zorunludur'
      } else if (form.value.fullName.length < 3) {
        errors.value.fullName = 'İsim Soyisim en az 3 karakter olmalıdır'
      }
      break

    case 'email':
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!form.value.email) {
        errors.value.email = 'E-posta adresi zorunludur'
      } else if (!emailRegex.test(form.value.email)) {
        errors.value.email = 'Geçerli bir e-posta adresi giriniz'
      }
      break

    case 'profession':
      if (!form.value.profession) {
        errors.value.profession = 'Meslek seçimi zorunludur'
      }
      break

    case 'otherProfession':
      if (form.value.profession === 'other' && !form.value.otherProfession) {
        errors.value.otherProfession = 'Diğer meslek alanı zorunludur'
      }
      break

    case 'cv':
      if (!form.value.cv) {
        errors.value.cv = 'CV yüklemeniz zorunludur'
      }
      break
  }

  return !errors.value[field]
}

const validateForm = () => {
  let isValid = true
  
  // Tüm alanları validate et
  const fields = ['fullName', 'email', 'profession', 'cv']
  fields.forEach(field => {
    if (!validateField(field)) {
      isValid = false
    }
  })

  // Eğer "other" seçiliyse otherProfession'ı da kontrol et
  if (form.value.profession === 'other') {
    if (!validateField('otherProfession')) {
      isValid = false
    }
  }

  return isValid
}

// Form gönderimi
const handleSubmit = async () => {
  if (!validateForm()) {
    toast.error('Lütfen tüm alanları doğru şekilde doldurun')
    return
  }

  try {
    // CV form verilerini hazırla
    cvForm.value.name = form.value.fullName
    cvForm.value.email = form.value.email
    
    // Pozisyon bilgisini düzenle
    if (form.value.profession === 'other' && form.value.otherProfession) {
      cvForm.value.profession = form.value.otherProfession
    } else {
      const selectedProfession = professionOptions.find(p => p.value === form.value.profession)
      cvForm.value.profession = selectedProfession ? selectedProfession.label : ''
    }
    
    // İş tipini belirle
    cvForm.value.job_type = form.value.applicationType === 'job' ? 'job' : 'internship'
    
    // CV dosyasını ayarla
    if (form.value.cv) {
      setFile(form.value.cv)
    }
    
    // Kullanım koşulları kabul edildi mi
    cvForm.value.agreement = form.value.agreement
    
    // API isteği gönder
    const success = await submitCV()
    
    if (success) {
    // Formu sıfırla
    form.value = {
      fullName: '',
      email: '',
      profession: '',
      otherProfession: '',
      applicationType: '',
      cv: null,
      agreement: false
    }
    errors.value = {}
    reset()
    }
  } catch (error) {
    console.error('Form gönderme hatası:', error)
    toast.error('Form gönderilirken bir hata oluştu')
  }
}

const handleFileUpload = async () => {
  await open()
  if (files.value?.length) {
    const file = files.value[0]
    // Dosya boyutu kontrolü (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Dosya boyutu 5MB\'dan büyük olamaz')
      reset()
      return
    }
    form.value.cv = file
    // useCV composable'ına dosyayı gönder
    setFile(file)
    toast.success('CV başarıyla yüklendi')
  }
}

const removeFile = () => {
  form.value.cv = null
  // useCV composable'ından dosyayı kaldır
  setFile(null)
  reset()
}
</script>

<style>
/* Modern Form Stilleri */
.form-group {
  @apply relative mb-5;
}

.form-label {
  @apply block mb-1.5 text-sm font-medium text-gray-700 dark:text-gray-300;
}

.form-input {
  @apply w-full px-3.5 py-2.5 bg-white dark:bg-neutral-700 rounded-lg border-2 outline-none transition-all duration-300;
  @apply text-sm text-gray-900 dark:text-white;
  @apply border-gray-200 dark:border-neutral-600;
  @apply focus:border-primary dark:focus:border-primary focus:ring-2 focus:ring-primary/20;
  @apply hover:border-gray-300 dark:hover:border-neutral-500;
  @apply placeholder-gray-400 dark:placeholder-gray-500;
}

.form-select {
  @apply w-full px-3.5 py-2.5 bg-white dark:bg-neutral-700 rounded-lg border-2 outline-none transition-all duration-300;
  @apply text-left text-sm text-gray-900 dark:text-white;
  @apply border-gray-200 dark:border-neutral-600;
  @apply hover:border-gray-300 dark:hover:border-neutral-500;
  @apply focus:border-primary dark:focus:border-primary focus:ring-2 focus:ring-primary/20;
}

.dropdown-menu {
  @apply absolute z-10 w-full mt-1 bg-white dark:bg-neutral-700 rounded-lg shadow-lg;
  @apply border border-gray-200 dark:border-neutral-600;
  @apply animate-dropdown;
}

.dropdown-item {
  @apply w-full px-3.5 py-2.5 text-left text-sm transition-colors duration-200;
  @apply text-gray-900 dark:text-gray-100;
  @apply hover:bg-primary/5 hover:text-primary;
  @apply flex items-center;
}

.error-message {
  @apply text-xs text-red-500 mt-1.5;
}

/* Animasyonlar */
@keyframes dropdown {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-dropdown {
  animation: dropdown 0.2s ease-out;
}

/* Custom Scrollbar */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(var(--primary-rgb), 0.2) transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(var(--primary-rgb), 0.2);
  border-radius: 20px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background-color: rgba(var(--primary-rgb), 0.3);
}
</style> 